import { useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CheckCircle, Loader2 } from 'lucide-react';
import {
  AgentType,
  ErrorCode,
  LlmChatAttachmentDto,
  LlmChatMessageDto,
  LlmChatSessionDto,
  LlmChatSessionStatus,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { llmChatApi } from '@/lib/api/llm-chat';
import {
  buildInquiryCreateRoute,
  buildInquiryCreateState,
} from '@/lib/inquiry-create-navigation';
import { useLanguage } from '@/hooks/useLanguage';
import { useLlmChatConceptScope } from '@/hooks/useLlmChatConceptScope';
import { toast } from 'sonner';
import * as Sentry from "@sentry/react";

type LlmChatSessionQueryData = { session: LlmChatSessionDto; messages: LlmChatMessageDto[] };

interface LlmChatCompletionDialogProps {
  sessionId: string;
  agentType: AgentType;
  inquiryId?: string | null;
  chatAttachments: LlmChatAttachmentDto[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LlmChatCompletionDialog({
  sessionId,
  agentType,
  inquiryId,
  chatAttachments,
  open,
  onOpenChange,
}: LlmChatCompletionDialogProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const conceptIdFromUrl = useLlmChatConceptScope();
  const [isExtracting, setIsExtracting] = useState(false);

  const isOffer = agentType === AgentType.OfferAdvisor;

  if (agentType === AgentType.InquirerGuide || agentType === AgentType.Generic) {
    return null;
  }

  const handleComplete = async () => {
    if (inquiryId && !isOffer) {
      onOpenChange(false);
      toast.error(t('llmChat.linkedInquiryNoCreate'));
      return;
    }

    setIsExtracting(true);
    try {
      const draft = await llmChatApi.completeSession(sessionId, language);

      queryClient.setQueryData<LlmChatSessionQueryData>(['llm-chat-session', sessionId], (old) =>
        old
          ? {
              ...old,
              session: {
                ...old.session,
                status: LlmChatSessionStatus.Completed,
                resultData: draft as LlmChatSessionDto['resultData'],
              },
            }
          : old,
      );
      queryClient.setQueryData<LlmChatSessionDto[]>(['llm-chat-sessions'], (old) =>
        old?.map((s) =>
          s.id === sessionId
            ? { ...s, status: LlmChatSessionStatus.Completed, resultData: draft as LlmChatSessionDto['resultData'] }
            : s,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['llm-chat-session', sessionId] });

      onOpenChange(false);
      if (isOffer && inquiryId) {
        const state = { chatDraft: draft, chatSessionId: sessionId, chatAttachments };
        navigate(`/inquiries/${inquiryId}/offers/new`, { state });
        toast.success(t('llmChat.sessionCompleted'));
      } else if (!inquiryId) {
        const state = buildInquiryCreateState({
          chatDraft: draft,
          chatSessionId: sessionId,
          chatAttachments,
        });
        navigate(buildInquiryCreateRoute(conceptIdFromUrl), { state });
        toast.success(t('llmChat.sessionCompleted'));
      } else {
        toast.error(t('llmChat.linkedInquiryNoCreate'));
      }
    } catch (err: unknown) {
      const ax = axios.isAxiosError(err) ? err : null;
      const status = ax?.response?.status;
      const code = ax?.response?.data?.code as number | undefined;

      if (status === 503) {
        toast.error(t('llmChat.error.serviceBusy'), { duration: undefined, dismissible: true });
      } else if (code === ErrorCode.LlmChatSessionAlreadyCompleted) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['llm-chat-sessions'] }),
          queryClient.invalidateQueries({ queryKey: ['llm-chat-session', sessionId] }),
        ]);
        onOpenChange(false);
        toast.error(t('llmChat.completionError.alreadyCompleted.title'), {
          description: t('llmChat.completionError.alreadyCompleted.description'),
          duration: undefined,
          dismissible: true,
        });
      } else if (code === ErrorCode.LlmChatSessionNoMessages) {
        toast.error(t('llmChat.completionError.noMessages.title'), {
          description: t('llmChat.completionError.noMessages.description'),
          duration: undefined,
          dismissible: true,
        });
      } else {
        Sentry.captureException(err as Error);
        toast.error(t('llmChat.completionError.generic.title'), {
          description: t('llmChat.completionError.generic.description'),
          duration: undefined,
          dismissible: true,
        });
      }
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            {t('llmChat.completeSession')}
          </DialogTitle>
          <DialogDescription>
            {t(isOffer ? 'llmChat.completeSessionDescriptionOffer' : 'llmChat.completeSessionDescription')}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExtracting}>
            {t('llmChat.cancel')}
          </Button>
          <Button onClick={handleComplete} disabled={isExtracting}>
            {isExtracting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(isOffer ? 'llmChat.createOffer' : 'llmChat.createInquiry')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
